import { GameState }    from '../../core/GameState.js';
import { EventBus }     from '../../core/EventBus.js';
import { CARGO_TYPES, CARGO_CATEGORIES, canShipCarryCargo } from '../../config/cargoTypes.js';
import { WAREHOUSE_COSTS } from '../../config/warehouseCosts.js';
import { isFeatureUnlocked, crewHireBlockedReason } from '../../config/research.js';
import { formatMoney, escapeHtml } from '../../utils/formatting.js';
import { describeHandling, formatHandlingTime } from '../../utils/cargoHandling.js';
import { renderContractCards, bindContractCardHandlers } from './contractCards.js';
import { drydockCardHtml, detentionNoteHtml } from './maintenanceInfo.js';
import { showAlert }    from '../components/dialogs.js';
import { CREW_POOL_CONFIG } from '../../config/constants.js';
import { serverIntent } from '../../core/serverIntent.js';

/**
 * Side panel that opens when a port is clicked on the map.
 * Sub-tabs: Trade | Services | Info
 *
 * Phase 6: all mutating actions (buy/sell cargo, refuel, repair, hire crew)
 * now call serverIntent() instead of local system methods.
 */
export class PortPanel {
  _tradeSortKey = 'stock';
  _tradeSortDir = 'desc';
  _tradeCategoryFilter = 'all';

  constructor(uiManager) {
    this.ui   = uiManager;
    this.port = null;
  }

  render(port, defaultTab, highlightCrew) {
    // Re-rendering the same port (e.g. a ship arrived) keeps the open tab
    const samePort = this.port?.id === port.id;
    this.port = port;
    this._highlightCrew = !!highlightCrew;
    const contractCount = this._getPortContracts().length;
    let startTab = defaultTab || (samePort && this._activeTab) || 'trade';
    if (startTab === 'contracts' && contractCount === 0) startTab = 'trade';
    const el  = document.getElementById('side-panel');
    el.innerHTML = `
      <div class="port-panel-header">
        <h3>${port.name}</h3>
        <button class="btn btn-sm close-port-panel">&times;</button>
      </div>
      <p class="port-sub">${port.country} &mdash; ${port.size} port</p>
      <nav class="port-tabs">
        <button class="ptab${startTab === 'trade' ? ' active' : ''}" data-ptab="trade">Trade</button>
        <button class="ptab${startTab === 'services' ? ' active' : ''}"        data-ptab="services">Services</button>
        <button class="ptab${startTab === 'info' ? ' active' : ''}"        data-ptab="info">Info</button>
        ${contractCount ? `<button class="ptab${startTab === 'contracts' ? ' active' : ''}" data-ptab="contracts">Contracts (${contractCount})</button>` : ''}
      </nav>
      <div class="port-tab-content"></div>`;

    el.querySelector('.close-port-panel').addEventListener('click', () => this.ui.closePortPanel());
    el.querySelectorAll('.ptab').forEach(btn => this._bindTabButton(btn));

    this._renderTab(startTab);
  }

  _bindTabButton(btn) {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.ptab').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      this._renderTab(btn.dataset.ptab);
    });
  }

  _renderTab(tab) {
    const el = document.querySelector('.port-tab-content');
    this._activeTab = tab;
    switch (tab) {
      case 'trade':     this._renderTrade(el); break;
      case 'services':  this._renderServices(el); break;
      case 'info':      this._renderInfo(el); break;
      case 'contracts': this._renderContracts(el); break;
    }
  }

  // ---------- Contracts ----------
  /** Available contracts picking up at this port, best paying first */
  _getPortContracts() {
    return (GameState.contracts?.available || [])
      .filter(c => c.originPort === this.port?.id)
      .sort((a, b) => b.payment - a.payment);
  }

  _renderContracts(el) {
    const items = this._getPortContracts();
    el.innerHTML = `<div class="contract-list">${renderContractCards(items, 'available')}</div>`;
    bindContractCardHandlers(el, () => this.render(this.port));
  }

  /** A ship here changed fuel/condition/maintenance — keep the Services tab current. */
  refreshServices(shipId) {
    if (this._activeTab !== 'services' || !this.port || !document.querySelector('.port-tabs')) return;
    if (GameState.getShip(shipId)?.currentPortId !== this.port.id) return;
    this._renderTab('services');
  }

  /** Contract pool changed: refresh the Contracts tab and its count, without
   *  re-rendering (and losing inputs on) whichever other tab is open. */
  refreshContracts() {
    const nav = document.querySelector('.port-tabs');
    if (!this.port || !nav) return;
    if (this._activeTab === 'contracts') { this.render(this.port); return; }

    const count = this._getPortContracts().length;
    let btn = nav.querySelector('.ptab[data-ptab="contracts"]');
    if (count === 0) { btn?.remove(); return; }
    if (!btn) {
      btn = document.createElement('button');
      btn.className = 'ptab';
      btn.dataset.ptab = 'contracts';
      this._bindTabButton(btn);
      nav.appendChild(btn);
    }
    btn.textContent = `Contracts (${count})`;
  }

  /** Get docked ships (not anchored) sorted most-recently-arrived first */
  _getDockedShips() {
    const ids = [...(this.port.dockedShips || [])].reverse();
    return ids.map(id => {
      const ship = GameState.getShip(id);
      if (!ship || ship.status !== 'docked') return null;
      return ship;
    }).filter(Boolean);
  }

  /** Resolve which ship is selected: keep previous if valid, else pick top docked */
  _resolveSelectedShip(dockedShips) {
    const prevId = this._selectedTradeShipId || '';
    const prev = prevId ? GameState.getShip(prevId) : null;
    if (prev && prev.status === 'docked' && prev.currentPortId === this.port.id) {
      return prev;
    }
    const auto = dockedShips.length > 0 ? dockedShips[0] : null;
    this._selectedTradeShipId = auto ? auto.id : '';
    return auto;
  }

  // ---------- Trade ----------
  _renderTrade(el) {
    const prices = GameState.marketPrices.get(this.port.id) || {};
    const dockedShips = this._getDockedShips();
    const selectedShip = this._resolveSelectedShip(dockedShips);

    let shipSelect = '<select id="trade-ship-select">';
    if (dockedShips.length === 0) {
      shipSelect += '<option value="">— No ships docked —</option>';
    }
    for (const s of dockedShips) {
      const selected = s.id === this._selectedTradeShipId ? ' selected' : '';
      shipSelect += `<option value="${s.id}"${selected}>${s.name} (${s.getAvailableCapacity()}/${s.getCargoCapacity()} TEU free)</option>`;
    }
    shipSelect += '</select>';

    const portStock = GameState.portStock.get(this.port.id) || {};
    const effectiveLoadFee = selectedShip ? selectedShip.getLoadingFeePerTEU(this.port) : this.port.loadingFeePerTEU;
    const handlingNote = describeHandling(selectedShip);
    const handlingRate = selectedShip ? Math.round(1 / selectedShip.estimateHandlingHours(this.port, 1, false)) : 0;
    const availCap = selectedShip ? selectedShip.getAvailableCapacity() : 0;

    const sortArrow = (key) => {
      if (this._tradeSortKey !== key) return '';
      return `<span class="sort-arrow">${this._tradeSortDir === 'desc' ? '▼' : '▲'}</span>`;
    };

    let buyEntries = Object.entries(CARGO_TYPES)
      .filter(([id, def]) => {
        if ((portStock[id] || 0) <= 0) return false;
        if (this._tradeCategoryFilter === 'ship') {
          if (!selectedShip || !canShipCarryCargo(selectedShip.type, id)) return false;
        } else if (this._tradeCategoryFilter !== 'all' && def.category !== this._tradeCategoryFilter) return false;
        return true;
      });

    buyEntries.sort((a, b) => {
      const [idA, defA] = a;
      const [idB, defB] = b;
      let cmp = 0;
      switch (this._tradeSortKey) {
        case 'stock': cmp = (portStock[idA] || 0) - (portStock[idB] || 0); break;
        case 'name':  cmp = defA.name.localeCompare(defB.name); break;
        case 'price': cmp = ((prices[idA] || defA.basePrice) + effectiveLoadFee) - ((prices[idB] || defB.basePrice) + effectiveLoadFee); break;
      }
      return this._tradeSortDir === 'desc' ? -cmp : cmp;
    });

    let buyRows = buyEntries.map(([id, def]) => {
      const stock = portStock[id] || 0;
      const canCarry = selectedShip && canShipCarryCargo(selectedShip.type, id);
      const disabled = !canCarry;
      const price = prices[id] || def.basePrice;
      const totalPricePerUnit = price + effectiveLoadFee;
      const maxAfford = totalPricePerUnit > 0 ? Math.floor((GameState.player?.money || 0) / totalPricePerUnit) : 0;
      const maxQty = disabled ? 0 : Math.min(stock, availCap, maxAfford);
      const noCapacity = maxQty <= 0 && !disabled;
      const rowDisabled = disabled || noCapacity;
      return `<div class="trade-buy-item${rowDisabled ? ' disabled' : ''}">
        <div class="trade-buy-info">
          <span class="trade-buy-name">${def.name}</span>
          <span class="trade-buy-price">${formatMoney(price)}</span>
          <span class="trade-buy-stock">${stock} TEU</span>
        </div>
        <div class="trade-buy-actions">
          <input type="number" class="trade-qty" data-cargo="${id}" data-price="${totalPricePerUnit}" data-max="${maxQty}" min="1" max="${maxQty}" value="${maxQty > 0 ? 1 : 0}" ${rowDisabled ? 'disabled' : ''}>
          <button class="btn-max" data-max-for="${id}" ${rowDisabled ? 'disabled' : ''}>Max</button>
          <span class="trade-total" data-total-for="${id}">${rowDisabled ? '' : formatMoney(totalPricePerUnit)}</span>
          <button class="btn btn-sm btn-primary" data-buy="${id}" ${rowDisabled ? 'disabled' : ''}>Buy</button>
        </div>
      </div>`;
    }).join('');

    let demandRows = Object.entries(CARGO_TYPES)
      .filter(([id, def]) => {
        if ((portStock[id] || 0) > 0) return false;
        // Port-terminal check (src/entities/Port.js's canHandleCargo) — the
        // buy list above is already implicitly filtered by this (stock for
        // a disallowed category never accumulates, see EconomySystem.js's
        // ambient-trickle gate), but this "sell here" list isn't stock-
        // driven, so it needs the check explicitly.
        if (!this.port.canHandleCargo(id)) return false;
        if (this._tradeCategoryFilter === 'ship') {
          if (!selectedShip || !canShipCarryCargo(selectedShip.type, id)) return false;
        } else if (this._tradeCategoryFilter !== 'all' && def.category !== this._tradeCategoryFilter) return false;
        return true;
      })
      .map(([id, def]) => {
        const price = prices[id] || def.basePrice;
        const isProduced = this.port.producedGoods.includes(id);
        const isDemanded = this.port.demandedGoods.includes(id);
        const sellMod = isProduced ? 0.5 : 0.9;
        const sellPrice = Math.round(price * sellMod);
        const tag = isProduced ? ' <span class="produced-tag">-50%</span>'
                  : isDemanded ? ' <span class="demand-tag">demand</span>' : '';
        return `<tr>
          <td>${def.name}${tag}</td>
          <td>${formatMoney(sellPrice)}</td>
        </tr>`;
      }).join('');

    let shipCargoHtml = '';
    if (selectedShip && selectedShip.cargo.length > 0) {
      shipCargoHtml = '<div class="ship-cargo-list">';
      selectedShip.cargo.forEach((c, i) => {
        const def = CARGO_TYPES[c.type];
        const basePrice = prices[c.type] || def?.basePrice || 100;
        const isProduced = this.port.producedGoods.includes(c.type);
        const sellMod = isProduced ? 0.5 : 0.9;
        const sellPrice = Math.round(basePrice * sellMod);
        const totalValue = sellPrice * c.quantity;
        const paidTotal = (c.purchasePrice || 0) * c.quantity;
        const profit = totalValue - paidTotal;
        const profitClass = profit >= 0 ? 'cargo-profit' : 'cargo-loss';
        const producedTag = isProduced ? ' <span class="produced-tag" title="This port produces this good — sell price reduced to 50%">-50%</span>' : '';
        // A ship can carry cargo it picked up elsewhere that this port has
        // no terminal for at all (src/entities/Port.js's canHandleCargo) —
        // TradeSystem.sellCargo() would hard-reject it, so disable the
        // button here instead of letting the player hit that error.
        const noTerminal = !this.port.canHandleCargo(c.type);
        shipCargoHtml += `<div class="cargo-sell-row">
          <span class="cargo-info">
            ${c.quantity} TEU <strong>${def?.name || c.type}</strong>${producedTag}
            <span class="cargo-paid">Paid ${formatMoney(paidTotal)}</span>
            ${noTerminal ? '<span class="cargo-paid" title="This port has no terminal for this cargo category">No terminal here</span>' : ''}
          </span>
          <span class="cargo-value">
            ${formatMoney(totalValue)}
            <span class="${profitClass}">${profit >= 0 ? '+' : ''}${formatMoney(profit)}</span>
          </span>
          <button class="btn btn-sm btn-danger" data-sell-idx="${i}" ${noTerminal ? 'disabled' : ''}>Sell</button>
        </div>`;
      });
      shipCargoHtml += '</div>';
    } else if (selectedShip) {
      shipCargoHtml = '<p class="panel-empty">Ship cargo hold is empty.</p>';
    } else {
      shipCargoHtml = '<p class="panel-empty">Select a ship to see cargo.</p>';
    }

    let filterButtons = `<button class="bfilter${this._tradeCategoryFilter === 'all' ? ' active' : ''}" data-cat="all">All</button>`;
    if (selectedShip) {
      filterButtons += `<button class="bfilter${this._tradeCategoryFilter === 'ship' ? ' active' : ''}" data-cat="ship">This Ship</button>`;
    }
    for (const [catId, catDef] of Object.entries(CARGO_CATEGORIES)) {
      filterButtons += `<button class="bfilter${this._tradeCategoryFilter === catId ? ' active' : ''}" data-cat="${catId}">${catDef.name}</button>`;
    }

    el.innerHTML = `
      <div class="trade-sticky-top">
        <div class="trade-header">
          ${shipSelect}
          <p class="player-funds">Funds: <strong>${formatMoney(GameState.player?.money || 0)}</strong></p>
        </div>
        ${handlingNote ? `<p class="trade-handling">⏳ ${escapeHtml(handlingNote)}</p>` : ''}
        <div class="trade-filters">${filterButtons}</div>
      </div>

      <div class="trade-section">
        <h5>Ship Cargo ${selectedShip ? `(${selectedShip.cargoCapacityUsed}/${selectedShip.getCargoCapacity()} TEU)` : ''}</h5>
        ${shipCargoHtml}
      </div>

      <div class="trade-section">
        <div class="trade-section-header">
          <h5>For Sale <span class="trade-load-fee">(+${formatMoney(effectiveLoadFee)}/TEU${handlingRate ? ` · loads ~${handlingRate} TEU/hr` : ''})</span></h5>
          <div class="trade-sort-btns">
            <button class="btn-xs sortable" data-sort="name">Name${sortArrow('name')}</button>
            <button class="btn-xs sortable" data-sort="price">Price${sortArrow('price')}</button>
            <button class="btn-xs sortable" data-sort="stock">Stock${sortArrow('stock')}</button>
          </div>
        </div>
        ${buyRows ? `<div class="trade-buy-list">${buyRows}</div>` : `<p class="panel-empty">${selectedShip ? 'No compatible goods for sale.' : 'No goods for sale.'}</p>`}
      </div>

      <div class="trade-section">
        <h5>Port Buys</h5>
        ${demandRows ? `<table class="trade-table trade-table-demand">
          <tr><th>Cargo</th><th>Pays/TEU</th></tr>
          ${demandRows}
        </table>` : '<p class="panel-empty">No additional goods wanted.</p>'}
      </div>`;

    const shipSelectEl = document.getElementById('trade-ship-select');
    shipSelectEl.addEventListener('change', () => {
      this._selectedTradeShipId = shipSelectEl.value;
      this._renderTab('trade');
    });

    el.querySelectorAll('.sortable').forEach(th => {
      th.addEventListener('click', () => {
        const key = th.dataset.sort;
        if (this._tradeSortKey === key) {
          this._tradeSortDir = this._tradeSortDir === 'desc' ? 'asc' : 'desc';
        } else {
          this._tradeSortKey = key;
          this._tradeSortDir = key === 'name' ? 'asc' : 'desc';
        }
        this._renderTab('trade');
      });
    });

    el.querySelectorAll('.trade-filters .bfilter').forEach(btn => {
      btn.addEventListener('click', () => {
        this._tradeCategoryFilter = btn.dataset.cat;
        this._renderTab('trade');
      });
    });

    el.querySelectorAll('.trade-qty').forEach(input => {
      input.addEventListener('input', () => {
        const maxQty = Number(input.dataset.max) || 0;
        let qty = Number(input.value) || 0;
        if (qty > maxQty) { qty = maxQty; input.value = maxQty; }
        if (qty < 1 && maxQty > 0) { qty = 1; }
        const price = Number(input.dataset.price);
        const total = price * qty;
        const cargoId = input.dataset.cargo;
        const totalEl = el.querySelector(`[data-total-for="${cargoId}"]`);
        if (totalEl) totalEl.textContent = formatMoney(total);
      });
    });

    el.querySelectorAll('.btn-max').forEach(btn => {
      btn.addEventListener('click', () => {
        const cargoId = btn.dataset.maxFor;
        const input = el.querySelector(`.trade-qty[data-cargo="${cargoId}"]`);
        if (!input) return;
        const maxQty = Number(input.dataset.max) || 0;
        if (maxQty <= 0) return;
        input.value = maxQty;
        const price = Number(input.dataset.price);
        const totalEl = el.querySelector(`[data-total-for="${cargoId}"]`);
        if (totalEl) totalEl.textContent = formatMoney(price * maxQty);
      });
    });

    // Buy handlers — send to server
    el.querySelectorAll('[data-buy]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const shipId  = document.getElementById('trade-ship-select').value;
        const cargoId = btn.dataset.buy;
        const qty     = Number(btn.closest('.trade-buy-item').querySelector('.trade-qty').value);
        if (!shipId) { showAlert('Select a ship first.'); return; }
        btn.disabled = true;
        const result = await serverIntent('trade/buy', {
          shipId, portId: this.port.id, cargoType: cargoId, quantity: qty
        });
        btn.disabled = false;
        if (result.success) {
          this._renderTab('trade');
        }
        // serverIntent shows toast on failure automatically
      });
    });

    // Sell handlers — send to server
    el.querySelectorAll('[data-sell-idx]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const shipId = this._selectedTradeShipId;
        const idx = Number(btn.dataset.sellIdx);
        if (!shipId) { showAlert('No ship selected.'); return; }
        btn.disabled = true;
        const result = await serverIntent('trade/sell', {
          shipId, portId: this.port.id, cargoIndex: idx
        });
        btn.disabled = false;
        if (result.success) {
          this._renderTab('trade');
        }
      });
    });
  }

  // ---------- Services ----------
  _renderServices(el) {
    const port  = this.port;
    const ships = this._getDockedShips();
    const selectedShip = this._resolveSelectedShip(ships);

    let shipSelect = '<select id="svc-ship-select">';
    if (ships.length === 0) {
      shipSelect += '<option value="">— No ships docked —</option>';
    }
    for (const s of ships) {
      const selected = s.id === this._selectedTradeShipId ? ' selected' : '';
      shipSelect += `<option value="${s.id}"${selected}>${s.name}</option>`;
    }
    shipSelect += '</select>';

    let fuelHtml = '<p>No fuel available</p>';
    if (port.services.fuel) {
      if (selectedShip) {
        const curFuel = Math.ceil(selectedShip.fuel);
        const maxFuel = Math.round(selectedShip.maxFuel);
        const isFull = (maxFuel - curFuel) < 1;
        if (isFull) {
          fuelHtml = `<div class="svc-card svc-card-block">
            <div class="svc-card-top"><strong>Fuel</strong> — ${formatMoney(port.fuelPrice)}/ton</div>
            <span class="svc-desc">Tank full</span>
          </div>`;
        } else {
          const defaultCost = Math.round((maxFuel - curFuel) * port.fuelPrice);
          fuelHtml = `<div class="svc-card svc-card-block">
            <div class="svc-card-top"><strong>Fuel</strong> — ${formatMoney(port.fuelPrice)}/ton</div>
            <div class="svc-slider-row">
              <input type="range" class="svc-range" id="fuel-range" min="${curFuel}" max="${maxFuel}" value="${maxFuel}" step="1">
              <input type="number" class="svc-amount" id="fuel-amount" min="${curFuel}" max="${maxFuel}" value="${maxFuel}" step="1">
              <span class="svc-unit">t</span>
            </div>
            <div class="svc-action-row">
              <span class="svc-cost-label" id="fuel-cost">${formatMoney(defaultCost)}</span>
              <button class="btn btn-sm btn-primary" id="btn-refuel">Refuel</button>
            </div>
            <span class="svc-desc">Full tank takes ~${formatHandlingTime(selectedShip.estimateBunkerHours(port, maxFuel - curFuel))} to pump (alongside loading)</span>
          </div>`;
        }
      } else {
        fuelHtml = `<div class="svc-card svc-card-block">
          <div class="svc-card-top"><strong>Fuel</strong> — ${formatMoney(port.fuelPrice)}/ton</div>
          <span class="svc-desc">Select a ship</span>
        </div>`;
      }
    }

    let repairHtml = '';
    if (port.services.repair) {
      if (selectedShip) {
        const curCond = Math.ceil(selectedShip.condition);
        const costPerPoint = port.getRepairCostPerPoint(selectedShip.type);
        const isFull = curCond >= 100;
        if (isFull) {
          repairHtml = `<div class="svc-card svc-card-block">
            <div class="svc-card-top"><strong>Repair</strong> — ${formatMoney(costPerPoint)}/point</div>
            <span class="svc-desc">No repair needed</span>
          </div>`;
        } else {
          const defaultCost = Math.round((100 - curCond) * costPerPoint);
          repairHtml = `<div class="svc-card svc-card-block">
            <div class="svc-card-top"><strong>Repair</strong> — ${formatMoney(costPerPoint)}/point</div>
            ${detentionNoteHtml(selectedShip)}
            <div class="svc-slider-row">
              <input type="range" class="svc-range" id="repair-range" min="${curCond}" max="100" value="100" step="1">
              <input type="number" class="svc-amount" id="repair-amount" min="${curCond}" max="100" value="100" step="1">
              <span class="svc-unit">%</span>
            </div>
            <div class="svc-action-row">
              <span class="svc-cost-label" id="repair-cost">${formatMoney(defaultCost)} · ~${formatHandlingTime(selectedShip.estimateRepairHours(port, 100 - curCond))}</span>
              <button class="btn btn-sm btn-primary" id="btn-repair">Repair</button>
            </div>
            <span class="svc-desc">Runs alongside loading — small repairs at each stop are much quicker than one big overhaul</span>
          </div>`;
        }
      } else {
        repairHtml = `<div class="svc-card svc-card-block">
          <div class="svc-card-top"><strong>Repair</strong></div>
          <span class="svc-desc">Select a ship</span>
        </div>`;
      }
    }

    const crewHtml = this._renderCrewHiringOffice(selectedShip);

    el.innerHTML = `
      ${shipSelect}
      <div class="services-list">
        ${fuelHtml}
        ${repairHtml}
        ${drydockCardHtml(selectedShip, port)}
        ${port.services.shipyard ? `<div class="svc-card">
          <strong>Ship Broker</strong>
          <span class="svc-desc">Buy &amp; sell vessels</span>
          <button class="btn btn-sm btn-primary" id="btn-shipyard">Open Broker</button>
        </div>` : ''}
        ${isFeatureUnlocked('warehouses') && WAREHOUSE_COSTS[port.size] ? `<div class="svc-card">
          <strong>Warehouse</strong>
          <span class="svc-desc">Cargo storage facility</span>
          <button class="btn btn-sm btn-primary" id="btn-warehouse">Open Warehouse</button>
        </div>` : ''}
        ${crewHtml}
      </div>`;

    const svcSelectEl = document.getElementById('svc-ship-select');
    svcSelectEl.addEventListener('change', () => {
      this._selectedTradeShipId = svcSelectEl.value;
      this._renderTab('services');
    });

    this._syncSliderPair('fuel-range', 'fuel-amount', 'fuel-cost', (target) => {
      if (!selectedShip) return 0;
      const delta = target - Math.ceil(selectedShip.fuel);
      return Math.round(delta * port.fuelPrice);
    });

    const repairCostPerPoint = selectedShip ? port.getRepairCostPerPoint(selectedShip.type) : 100;
    const repairTimeText = (target) => {
      const points = target - Math.ceil(selectedShip?.condition ?? target);
      return points > 0 ? ` · ~${formatHandlingTime(selectedShip.estimateRepairHours(port, points))}` : '';
    };
    this._syncSliderPair('repair-range', 'repair-amount', 'repair-cost', (target) => {
      if (!selectedShip) return 0;
      const delta = target - Math.ceil(selectedShip.condition);
      return Math.round(delta * repairCostPerPoint);
    }, repairTimeText);

    // Refuel — server intent
    const refuelBtn = document.getElementById('btn-refuel');
    if (refuelBtn) {
      refuelBtn.addEventListener('click', async () => {
        const shipId = document.getElementById('svc-ship-select').value;
        if (!shipId) { showAlert('Select a ship.'); return; }
        const ship = GameState.getShip(shipId);
        if (!ship) return;
        const target = Number(document.getElementById('fuel-amount').value);
        const amount = target - ship.fuel;
        if (amount < 0.5) return;
        refuelBtn.disabled = true;
        await serverIntent('ship/refuel', { shipId, portId: port.id, amount });
        refuelBtn.disabled = false;
        this._renderTab('services');
      });
    }

    // Dry dock — server intent
    const drydockBtn = document.getElementById('btn-drydock');
    if (drydockBtn && selectedShip) {
      drydockBtn.addEventListener('click', async () => {
        drydockBtn.disabled = true;
        await serverIntent('ship/drydock', { shipId: selectedShip.id });
        drydockBtn.disabled = false;
        this._renderTab('services');
      });
    }

    // Repair — server intent
    const repairBtn = document.getElementById('btn-repair');
    if (repairBtn) {
      repairBtn.addEventListener('click', async () => {
        const shipId = document.getElementById('svc-ship-select').value;
        if (!shipId) { showAlert('Select a ship.'); return; }
        const ship = GameState.getShip(shipId);
        if (!ship) return;
        const target = Number(document.getElementById('repair-amount').value);
        repairBtn.disabled = true;
        await serverIntent('ship/repair', {
          shipId, portId: port.id, targetCondition: target
        });
        repairBtn.disabled = false;
        this._renderTab('services');
      });
    }

    const shipyardBtn = document.getElementById('btn-shipyard');
    if (shipyardBtn) {
      shipyardBtn.addEventListener('click', () => EventBus.emit('ui:shipyardOpen', { port: this.port }));
    }

    const warehouseBtn = document.getElementById('btn-warehouse');
    if (warehouseBtn) {
      warehouseBtn.addEventListener('click', () => EventBus.emit('ui:warehouseOpen', { port: this.port }));
    }

    this._attachCrewHireHandlers(selectedShip);

    if (this._highlightCrew) {
      this._highlightCrew = false;
      const crewCard = el.querySelector('.crew-hiring-office');
      if (crewCard) {
        crewCard.classList.add('svc-card-flash');
        crewCard.addEventListener('animationend', () => crewCard.classList.remove('svc-card-flash'), { once: true });
        crewCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  _renderCrewHiringOffice(selectedShip) {
    // Read pool from GameState (populated from snapshot portCrewPools)
    const pool = GameState.portCrewPools.get(this.port.id) || [];

    const shipCrew = pool.filter(c => c.role === 'navigator' || c.role === 'engineer');
    const analysts = pool.filter(c => c.role === 'market_analyst');

    const maxSlots = selectedShip?.type?.extraCrewSlots || 0;
    const usedSlots = selectedShip
      ? (selectedShip.crew.navigator ? 1 : 0) + (selectedShip.crew.engineer ? 1 : 0)
      : 0;
    const slotsAvailable = maxSlots - usedSlots;
    const hasAnalyst = !!GameState.marketAnalyst;

    const contractDays = CREW_POOL_CONFIG.CONTRACT_MIN_DAYS;

    let crewRows = '';
    if (shipCrew.length === 0 && analysts.length === 0) {
      crewRows = '<p class="panel-empty">No crew available at this port.</p>';
    } else {
      if (shipCrew.length > 0) {
        crewRows += '<div class="crew-hire-group"><h6>Ship Crew</h6>';
        for (const c of shipCrew) {
          const roleLabel = c.role === 'navigator' ? 'Navigator' : 'Engineer';
          const tierClass = `badge-${c.skillTier}`;
          const slotFilled = selectedShip && selectedShip.crew[c.role] !== null;
          const researchBlock = this._crewResearchBlock(c);
          const cantHire = !!researchBlock || !selectedShip || slotsAvailable <= 0 || slotFilled;
          const reason = researchBlock ? researchBlock
            : !selectedShip ? 'No ship selected'
            : slotFilled ? `${roleLabel} slot filled`
            : slotsAvailable <= 0 ? 'No crew slots' : '';
          crewRows += `
            <div class="crew-hire-row">
              <span class="crew-hire-name">${c.name}</span>
              <span class="crew-hire-role">${roleLabel}</span>
              <span class="badge ${tierClass}">${c.skillTier}</span>
              <span class="crew-hire-salary">${formatMoney(c.dailySalary)}/day</span>
              <span class="crew-hire-contract">${contractDays}d</span>
              <button class="btn btn-xs btn-primary crew-hire-btn" data-hire-crew="${c.id}" ${cantHire ? `disabled title="${reason}"` : ''}>Hire</button>
            </div>`;
        }
        crewRows += '</div>';
      }

      if (analysts.length > 0) {
        crewRows += '<div class="crew-hire-group"><h6>Market Analysts</h6>';
        for (const a of analysts) {
          const tierClass = `badge-${a.skillTier}`;
          const researchBlock = this._crewResearchBlock(a);
          const cantHire = hasAnalyst || !!researchBlock;
          const reason = researchBlock || 'Already have an analyst';
          crewRows += `
            <div class="crew-hire-row">
              <span class="crew-hire-name">${a.name}</span>
              <span class="crew-hire-role">Analyst</span>
              <span class="badge ${tierClass}">${a.skillTier}</span>
              <span class="crew-hire-salary">${formatMoney(a.dailySalary)}/day</span>
              <span class="crew-hire-contract">${contractDays}d</span>
              <button class="btn btn-xs btn-primary crew-hire-btn" data-hire-analyst="${a.id}" ${cantHire ? `disabled title="${reason}"` : ''}>Hire</button>
            </div>`;
        }
        crewRows += '</div>';
      }
    }

    return `<div class="svc-card svc-card-block crew-hiring-office">
      <div class="svc-card-top"><strong>Crew Hiring Office</strong></div>
      ${crewRows}
    </div>`;
  }

  /** Missing-research reason for hiring this candidate, or null (server enforces too). */
  _crewResearchBlock(candidate) {
    return crewHireBlockedReason(candidate.role, candidate.skillTier,
      GameState.research?.completed, GameState.permits?.completed);
  }

  _attachCrewHireHandlers(selectedShip) {
    // Ship crew hire — server intent
    document.querySelectorAll('[data-hire-crew]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const crewId = btn.dataset.hireCrew;
        const shipId = document.getElementById('svc-ship-select')?.value;
        if (!shipId) { showAlert('Select a ship first.'); return; }
        btn.disabled = true;
        const result = await serverIntent('crew/hire', {
          crewId, shipId, portId: this.port.id
        });
        btn.disabled = false;
        if (result.success) {
          EventBus.emit('ui:notification', {
            message: `Hired crew member (1-year contract)`,
            type: 'success'
          });
          this._renderTab('services');
        }
      });
    });

    // Analyst hire — server intent
    document.querySelectorAll('[data-hire-analyst]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const analystId = btn.dataset.hireAnalyst;
        btn.disabled = true;
        const result = await serverIntent('crew/hire-analyst', {
          analystId, portId: this.port.id
        });
        btn.disabled = false;
        if (result.success) {
          EventBus.emit('ui:notification', {
            message: `Hired market analyst (1-year contract)`,
            type: 'success'
          });
          this._renderTab('services');
        }
      });
    });
  }

  /** Wire a range slider + number input pair for live cost updates */
  _syncSliderPair(rangeId, amountId, costId, calcCost, describe) {
    const range = document.getElementById(rangeId);
    const amount = document.getElementById(amountId);
    const costEl = document.getElementById(costId);
    if (!range || !amount || !costEl) return;

    const update = (val) => {
      const min = Number(range.min);
      const max = Number(range.max);
      val = Math.max(min, Math.min(max, Math.round(val)));
      range.value = val;
      amount.value = val;
      const cost = calcCost(val);
      costEl.textContent = (cost > 0 ? formatMoney(cost) : '$0') + (describe ? describe(val) : '');
    };

    range.addEventListener('input', () => update(Number(range.value)));
    amount.addEventListener('input', () => update(Number(amount.value)));
  }

  // ---------- Info ----------
  _renderInfo(el) {
    const p = this.port;
    const used = p.dockedShips.size;
    const total = p.berthCount;
    const pct = total > 0 ? used / total : 0;
    const berthColor = pct >= 1 ? 'var(--red, #e74c3c)' : pct > 0.75 ? 'var(--orange, #e67e22)' : '';
    const berthStyle = berthColor ? ` style="color:${berthColor};font-weight:600"` : '';
    const queueCount = p.anchorQueue ? p.anchorQueue.length : 0;
    const queueRow = queueCount > 0
      ? `<tr><td>Queue</td><td style="color:var(--orange, #e67e22)">${queueCount} ship${queueCount !== 1 ? 's' : ''} waiting</td></tr>`
      : '';
    el.innerHTML = `
      <table class="stats-table">
        <tr><td>Country</td>       <td>${p.country}</td></tr>
        <tr><td>Region</td>        <td>${p.region}</td></tr>
        <tr><td>Size</td>          <td>${p.size}</td></tr>
        <tr><td>Berths</td>        <td${berthStyle}>${used}/${total}</td></tr>
        ${queueRow}
        <tr><td>Wealth</td>        <td>${p.wealthLevel}/10</td></tr>
        <tr><td>Industrial</td>    <td>${p.industrialLevel}/10</td></tr>
        <tr><td>Population</td>    <td>${p.populationLevel}/10</td></tr>
        <tr><td>Produces</td>      <td>${p.producedGoods.join(', ') || '—'}</td></tr>
        <tr><td>Demands</td>       <td>${p.demandedGoods.join(', ') || '—'}</td></tr>
        <tr><td>Fuel Price</td>    <td>${formatMoney(p.fuelPrice)}/t</td></tr>
        <tr><td>Loading Fee</td>   <td>${formatMoney(p.loadingFeePerTEU)}/TEU</td></tr>
        <tr><td>Docking Fee</td>   <td>${formatMoney(p.dockingFee)}</td></tr>
        <tr><td>Max Ship Size</td> <td>${p.maxShipCapacity >= 99999 ? 'Unlimited' : p.maxShipCapacity + ' TEU'}</td></tr>
        <tr><td>Services</td>      <td>${Object.entries(p.services).filter(([,v])=>v).map(([k])=>k).join(', ')}</td></tr>
      </table>`;
  }
}
